import PropTypes from "prop-types";
import React from "react";

import useOutputReportStore from "../../../../store/OutputReport.store";
import { getOutputType } from "../../../../utils/helpers/dataTable";
import BarChart from "../../../Chart/Bar";
import LineChart from "../../../Chart/Line";

import {
  createChartLegend,
  getChartOptions,
  getColorSequence,
  getDatasetsMinMaxValue,
  getReversedChartDataset,
  getStepSizeAndLabels,
  getXlabels,
  getYlabels,
} from "../../../../utils/helpers/chart";

const CouponCashFlowChart = ({ id, isFullWidth }) => {
  const reportName = "Cash Flow Profile";
  const data = useOutputReportStore((state) => state.data);
  const { reportType } = getOutputType(data);

  const couponCashFlowConfig = {
    transition: {
      isFullWidth,
      isLegend: true,
      targetColumnKeyValues: [],
      tooltipPosition: "average",
      xAnnotationSpace: 3,
      xLabelSpacebetween: 3,
      xAnnotationType: "type-1",
      isXannotationCentered: false,
      yTitle: "Cash Flow",
      yGridEnabled: false,
    },
    cash: {
      barPercentage: 0.7,
      borderRadius: 5,
      isFullWidth,
      isLegend: false,
      targetColumnKeyValues: ["Income"],
      xAnnotationSpace: 1,
      xLabelSpacebetween: 1,
      xAnnotationType: "type-1",
      isXannotationCentered: true,
      xTitle: "Month",
      xLabelOffset: 0,
      yTitle: "Income",
      tooltipValueType: "currency",
      yTicksType: "currency",
      yGridEnabled: false,
    },
  };

  const excludedXLabels = {
    transition: [],
    cash: ["% Yearly Income", "% of Par Value"],
  };

  const excludedYLabels = {
    transition: ["total"],
    cash: ["total"],
  };

  const reportData = data[reportName];
  const isDate = true;
  const yLabels = getYlabels(reportData, excludedYLabels[reportType], isDate);
  const { xLabels } = getXlabels(reportData, excludedXLabels[reportType]);

  const chartId = `${id}-${reportType}`;
  const chartConfig = couponCashFlowConfig[reportType];

  // Colorset based on ReportType
  const withGray = reportType === "transition";
  const chartStyles = getColorSequence(withGray);

  // Chart Dataset
  const chartDatasets = getReversedChartDataset(
    reportData,
    yLabels,
    xLabels,
    isDate
  );

  // get dataset max
  const { max } = getDatasetsMinMaxValue(chartDatasets);
  //get step size and step labels
  const { stepSize, stepSizeLabels } = getStepSizeAndLabels(max);

  // Chart Option
  chartConfig.xLabels = yLabels;
  chartConfig.borderWidth = 2;
  chartConfig.yBeginAtZero = true;
  chartConfig.yStepSize = stepSize;
  chartConfig.yStepSizeLabels = stepSizeLabels;
  const chartOptions = getChartOptions(chartConfig);

  // Format Chart Data
  const chartData = {
    labels: yLabels,
    datasets: chartDatasets.map((item, idx) => {
      return {
        ...item,
        ...chartStyles[idx],
      };
    }),
  };

  // Chart Legend
  const chartLegend = createChartLegend({
    containerId: chartId,
    legendId: `legend-${chartId}`,
    legendTitle: "",
    isReversed: false,
    isFullWidth,
  });

  // Chart Props
  const chartProps = {
    id: chartId,
    data: chartData,
    options: chartOptions,
    plugins: chartConfig.isLegend ? [chartLegend] : [],
    ariaLabel: "Coupon Cash Flow Report Line Chart",
    height: isFullWidth ? "450px" : "372px",
  };

  const chartMap = {
    transition: LineChart,
    cash: BarChart,
  };
  const Chart = chartMap[reportType];

  return <Chart {...chartProps} />;
};

CouponCashFlowChart.propTypes = {
  id: PropTypes.string,
  isFullWidth: PropTypes.bool,
};

CouponCashFlowChart.defaultProps = {
  id: "line-chart-coupon-cash-flow",
  isFullWidth: true,
};

export default CouponCashFlowChart;
